import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import { ATTACHMENT_PARENT_TYPES } from 'features/shared/constants/attachmentParentTypes';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { useAttachmentDialog } from 'features/shared/hooks/useAttachmentDialog';
import {
  getAttachments,
  getAttachment,
  deleteAttachment,
} from '../../../shared/services/attachmentService';
import { ATTACHMENT_GRID_COLUMNS } from '../../constants/addressAttachmentsTabConstants';
import FormSpinner from 'components/FormSpinner';

const AddressAttachmentsTab = ({ ptsAddressID }) => {
  const dispatch = useDispatch();
  const canEditAddress = usePermissions(PERMISSION_RESOURCES.ADDRESS).Edit;
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const { openAttachmentDialog } = useAttachmentDialog();
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadAttachments = useCallback(async () => {
    if (!ptsAddressID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getAttachments({ id: ptsAddressID, type: ATTACHMENT_PARENT_TYPES.ADDRESS }),
      onSuccess: (data) => setAttachments(data),
    });
  }, [dispatch, ptsAddressID]);

  const handleAdd = () =>
    openAttachmentDialog({
      parentId: ptsAddressID,
      parentType: ATTACHMENT_PARENT_TYPES.ADDRESS,
      canSave: canEditAddress,
      onSaved: loadAttachments,
    });
  const handleEdit = (attachment) =>
    openAttachmentDialog({
      parentId: ptsAddressID,
      parentType: ATTACHMENT_PARENT_TYPES.ADDRESS,
      canSave: canEditAddress,
      attachment,
      onSaved: loadAttachments,
    });

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.FileName || 'attachment'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteAttachment(row.ptsAttachmentID),
            onSuccess: () => {
              dispatch(notify('Attachment deleted successfully', 'success'));
              loadAttachments();
            },
          });
        },
        'Delete Attachment'
      );
    },
    [dispatch, requestConfirm, loadAttachments]
  );

  const handleDownload = useCallback(
    async (row) => {
      await executeApiAction({
        setLoading,
        dispatch,
        action: async () => {
          const response = await getAttachment(row.ptsAttachmentID);
          const blob = new Blob([response.buffer], { type: response.type });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.download = row.FileName;
          link.click();

          window.URL.revokeObjectURL(url);
        },
      });
    },
    [dispatch]
  );

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  return (
    <Box className="person-names-tab__grid-wrap address-attachments-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Attachment"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditAddress}
        />
      </Box>

      <CommonDataGrid
        rows={attachments}
        columns={ATTACHMENT_GRID_COLUMNS}
        getRowId={(row) => row.ptsAttachmentID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Address Attachments' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditAddress },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditAddress },
          custom: [
            {
              icon: <GetAppIcon />,
              tooltip: 'Download',
              onClick: handleDownload,
            },
          ],
        }}
      />

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default AddressAttachmentsTab;
